// ExportFlow — the guided packaging flow, pure. Composes RadioGroup, Button,
// Text, Badge, ProgressBar, EmptyState.
//
// Replaces the app's mocked "Export material" dialog CONTENT (AppShellHost puts
// this inside a Dialog; the flow owns none of the dialog chrome). The author
// picks an author-facing Format ("Torus package", "Common Cartridge (Canvas)",
// "Markdown"), sees a short honest note of what will and won't survive that
// target (docs rich-content-and-media §C.3 — some media kinds degrade to a link
// on constrained targets, NEVER a silent drop), then packages and downloads.
//
// The flow's PROGRESS is a SUM, not a pile of booleans — `ExportState` has one
// variant per moment (choosing / review / packaging / done / failed), so an
// impossible mix ("packaging AND failed") is unrepresentable and the render is
// a total match over the discriminant. That state arrives as a PROP: the
// component is pure/props-driven and does no timers or I/O of its own — every
// transition is reported UP (onExport / onPackageAnyway / onReviewFirst /
// onDownload / onReset) and the owner (the app, or a story via useState)
// advances the state. The one piece the component holds is the in-progress
// format highlight while CHOOSING — ephemeral view state, not a side effect —
// committed upward only when the author packages.
//
// `review` (.claude/analysis/citation-load-bearing-spec.md §3/§4) sits between
// choosing and packaging: the export-boundary policy evaluation
// (`evaluateMaterialPolicy(..., exportAdvisory(format))`) flagged the factual
// axis. It renders a LOUD but OVERRIDABLE alarm — "Package anyway" always
// works, the alarm informs rather than gates (alarm-discipline) — showing
// EVIDENCE, never a percentage: broken citations (the hard, load-bearing
// signal) and, when non-trivial, the actual terms the material's content
// doesn't share with its sources. The overlap coverage NUMBER never appears
// here — see the honest-signal caveat in the spec.
//
// Register (.claude/research/design/06): near-monochrome, no resting borders. Colour is
// never the sole channel — the survival note pairs each status tint with an
// icon AND words ("Video → link"); the done/failed lines pair tone with a glyph
// and a sentence. The one accent spend is the primary "Export" / "Download".

import type * as Core from "@bilda/core";
import { exhaustSwitchCase } from "@bilda/utils";
import { Trans, useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { useState } from "react";
import type { ExportNextStep } from "../../app/exportFailure.js";
import {
    readLastExportFormat,
    rememberExportFormat,
    resolveInitialFormat,
} from "../../app/exportPrefs.js";
import { Badge } from "../Badge/Badge.js";
import { Button } from "../Button/Button.js";
import { EmptyState } from "../EmptyState/EmptyState.js";
import { ErrorBanner } from "../ErrorBanner/ErrorBanner.js";
import { GroundingAlarm } from "../GroundingAlarm/GroundingAlarm.js";
import { Icon } from "../Icon/Icon.js";
import { ProgressBar } from "../ProgressBar/ProgressBar.js";
import { RadioGroup, type RadioGroupOption } from "../RadioGroup/RadioGroup.js";
import { Text } from "../Text/Text.js";
import "./ExportFlow.css";

// How ONE media kind fares in a target Format. A discriminated union, not a
// boolean "supported" flag: "kept" and "degraded" are different outcomes with
// different data ("degraded" names what the kind BECOMES), so they are distinct
// variants. `kind` / `becomes` are caller-owned author-facing strings (the host
// resolves their i18n, like `label`/`hint`), so no macro is spent on them here.
export type MediaSupport =
    | { readonly type: "kept"; readonly kind: string }
    | {
          readonly type: "degraded";
          readonly kind: string;
          readonly becomes: string;
      };

// One author-facing export target. `label`/`hint` are author-facing prose (no
// dev jargon — "Common Cartridge (Canvas)", never "imscc"). `media` is the
// optional "what survives" note: a short list of per-kind outcomes shown as
// badges. All three text fields are caller-owned strings (host owns i18n).
export type ExportFormat = {
    readonly id: string;
    readonly label: string;
    readonly hint: string;
    readonly media?: readonly MediaSupport[];
    // When set, the format is shown but NOT selectable, with this one-line
    // reason (e.g. "Not available for this material"). Preserves the signal
    // that the format exists without letting the author pick one that would
    // dead-end in a failed export (REQ-FORMATS-001).
    readonly disabledReason?: string;
};

// The flow's progress as a sum — exactly one moment is true at a time.
//   choosing  → the author is picking a format.
//   review    → the export-boundary policy evaluation (`exportAdvisory`)
//               flagged the factual axis. A LOUD but OVERRIDABLE stop
//               between choosing and packaging — never a silent gate. Its
//               evidence is never a percentage (§3):
//                 - `unsupportedTerms` — content-bearing terms present in
//                   the material but absent from its sources (the overlap
//                   proxy's `unmatchedTop`), shown only when non-trivial.
//                   A soft, honest "look here", not a score to hit.
//               `evaluation` is the underlying `PolicyEvaluation` (drives
//               the advisory-vs-gate register line, matching
//               `FullQualityView`/`QualitySummary`).
//   packaging → the package is being built (amount unknown).
//   done      → a file is ready; `downloadName` names it.
//   failed    → packaging failed (FR-10.3): `message` says why, `fix` what
//               to change, `next` the one way on; `errorId` is the
//               copyable reference. `format` is host bookkeeping for retry.
export type ExportState =
    | { readonly type: "choosing" }
    | {
          readonly type: "review";
          readonly evaluation: Core.Policy.Evaluate.PolicyEvaluation;
          readonly unsupportedTerms: readonly string[];
          // The format the author chose — remembered so "Package anyway" can
          // proceed to it without a re-pick. Host bookkeeping (not rendered).
          readonly format: string;
      }
    | { readonly type: "packaging" }
    | { readonly type: "done"; readonly downloadName: string }
    | {
          readonly type: "failed";
          readonly message: string;
          readonly fix?: string;
          readonly errorId: string;
          readonly next: ExportNextStep;
          readonly format: string;
      };

// How the finished package leaves the app — a PRESENTATION register, not an
// intent split: both values report the same single `onDownload` intent, only
// the done-step verb copy changes (so this is deliberately not two commands).
//   "download" → web shell: the browser downloads the file ("Download").
//   "save"     → desktop shell: the host opens a native save dialog behind
//                onDownload ("Save to disk"; ui-design-synthesis §2/Export).
export type ExportDelivery = "download" | "save";

export type ExportFlowProps = {
    readonly formats: readonly ExportFormat[];
    readonly state: ExportState;
    // Which shell register the done-step copy takes. Defaults to "download"
    // (the web wording), so existing call sites are unchanged.
    readonly delivery?: ExportDelivery;
    // The format to preselect while choosing. The host is the proper injection
    // point; left unset, the flow falls
    // back to the author's last-used format (device pref, exportPrefs.ts) and
    // then to the first offered format. A value that is not among `formats` is
    // ignored by the same guard the last-used path uses.
    readonly initialFormatId?: string;
    // Commit: package the material as the chosen format. The only choosing→ transition.
    readonly onExport: (formatId: string) => void;
    // Fetch the finished package (offered while `done`).
    readonly onDownload?: () => void;
    // Return to the picker (offered while `done` and `failed`).
    readonly onReset?: () => void;
    // The review alarm's PRIMARY action — proceed to packaging despite the
    // flagged claims. Always enabled: the alarm informs, it never seizes the
    // decision (alarm-discipline). Offered only while `review`.
    readonly onPackageAnyway?: () => void;
    // The review alarm's secondary exit — leave the flow to inspect the
    // flagged claims (e.g. open the editor at the citing element) instead of
    // packaging now. Offered only while `review`.
    readonly onReviewFirst?: () => void;
    // Package the failed format again (offered while `failed`, next
    // "try-again").
    readonly onRetry?: () => void;
    readonly className?: string;
};

// A check — "this kind survives intact". currentColor, so it takes the tone of
// the Badge/Text that mounts it. Decorative: the label text carries the meaning.
const keptGlyph = <Icon name="check" size="xs" />;

// An arrow — "this kind is carried across AS something else" (e.g. → link).
const degradedGlyph = <Icon name="arrow-right" size="xs" />;

// A filled check — the success line's redundant (non-colour) channel.
const doneGlyph = <Icon name="check" />;

// One survival badge. A match over the outcome discriminant — a new outcome
// fails to compile until its badge is written. Both tones are STATUS tones, so
// Badge demands the icon (the redundant channel); the words say the rest.
const SupportBadge = ({ support }: { support: MediaSupport }): ReactNode => {
    switch (support.type) {
        case "kept":
            return (
                <Badge tone="success" icon={keptGlyph}>
                    {support.kind}
                </Badge>
            );
        case "degraded":
            return (
                <Badge tone="warning" icon={degradedGlyph}>
                    {support.kind}
                    <Icon name="arrow-right" size="xs" />
                    {support.becomes}
                </Badge>
            );
        default: {
            // Exhaustiveness: a new MediaSupport variant is `never` here.
            const exhaustive: never = support;
            return exhaustive;
        }
    }
};

// One format row's rich label for the RadioGroup: name, hint, and the optional
// survival note. Built as a node so the radio dial binds to the whole block.
const formatLabel = (format: ExportFormat): ReactNode => (
    <span className="bilda-export-flow__format">
        <Text weight="medium">{format.label}</Text>
        <Text size="sm" tone="dim">
            {format.hint}
        </Text>
        {format.media && format.media.length > 0 ? (
            <span className="bilda-export-flow__survives">
                {format.media.map((support) => (
                    <SupportBadge key={support.kind} support={support} />
                ))}
            </span>
        ) : null}
    </span>
);

export const ExportFlow = ({
    formats,
    state,
    delivery = "download",
    initialFormatId,
    onExport,
    onDownload,
    onReset,
    onPackageAnyway,
    onReviewFirst,
    onRetry,
    className,
}: ExportFlowProps): ReactNode => {
    const { t } = useLingui();
    // Ephemeral view state: which format is highlighted before the author
    // commits. Not a side effect — it never leaves the component until Export
    // reports it up via onExport. The lazy initializer runs ONCE, at mount:
    // it seeds from the host's `initialFormatId`, else the author's last-used
    // format (device pref), else the first offered format. `resolveInitialFormat`
    // is the pure guard — a remembered/injected id no longer in `formats`
    // silently falls back to `formats[0]` (never a stale selection). Reading the
    // pref is the one impure seam; it is isolated in exportPrefs.ts, mirroring
    // the material-create dialog seeding its title field from the clock.
    const [selected, setSelected] = useState<string>(() =>
        resolveInitialFormat(
            initialFormatId ?? readLastExportFormat(),
            formats,
        ),
    );
    const root = `bilda-export-flow${className ? ` ${className}` : ""}`;

    switch (state.type) {
        case "choosing": {
            // Only codec-backed formats are selectable; the rest are shown
            // disabled-with-reason (REQ-FORMATS-001), so the author keeps the
            // signal that they exist without being able to pick a dead-end.
            const selectable = formats.filter(
                (f) => f.disabledReason === undefined,
            );
            const unavailable = formats.filter(
                (f) => f.disabledReason !== undefined,
            );
            if (selectable.length === 0) {
                return (
                    <div className={root}>
                        <EmptyState
                            title={t`No export formats yet`}
                            body={t`This material can't be packaged for a learning context yet.`}
                        />
                    </div>
                );
            }
            // Guard against a stale highlight if the offered set changed (a
            // gated format must never remain the committed selection).
            const active = selectable.some((f) => f.id === selected)
                ? selected
                : (selectable[0]?.id ?? "");
            const options: readonly RadioGroupOption[] = selectable.map(
                (format) => ({
                    value: format.id,
                    label: formatLabel(format),
                }),
            );
            return (
                <div className={root}>
                    <RadioGroup
                        options={options}
                        value={active}
                        onValueChange={setSelected}
                        name="export-format"
                        ariaLabel={t`Export format`}
                    />
                    {unavailable.length > 0 ? (
                        <ul className="bilda-export-flow__unavailable">
                            {unavailable.map((format) => (
                                <li
                                    key={format.id}
                                    className="bilda-export-flow__unavailableRow"
                                >
                                    <Text weight="medium" tone="dim">
                                        {format.label}
                                    </Text>
                                    <Text size="sm" tone="dim">
                                        {format.disabledReason}
                                    </Text>
                                </li>
                            ))}
                        </ul>
                    ) : null}
                    <div className="bilda-export-flow__actions">
                        <Button
                            variant="primary"
                            onClick={() => {
                                // Remember the choice so the next export session
                                // preselects it (device pref, exportPrefs.ts),
                                // then commit the export upward.
                                rememberExportFormat(active);
                                onExport(active);
                            }}
                        >
                            <Trans>Export</Trans>
                        </Button>
                    </div>
                </div>
            );
        }

        case "review": {
            const { evaluation, unsupportedTerms } = state;
            return (
                <div className={root}>
                    <GroundingAlarm
                        unsupportedTerms={unsupportedTerms}
                        advisory={evaluation.enforcement === "advisory"}
                        primaryLabel={<Trans>Package anyway</Trans>}
                        onPrimary={() => onPackageAnyway?.()}
                        secondaryLabel={<Trans>Review first</Trans>}
                        onSecondary={onReviewFirst}
                    />
                </div>
            );
        }

        case "packaging":
            return (
                <div className={root}>
                    <div className="bilda-export-flow__status" role="status">
                        <ProgressBar label={t`Exporting`} />
                        <Text tone="dim">
                            <Trans>Exporting your material…</Trans>
                        </Text>
                    </div>
                </div>
            );

        case "done":
            return (
                <div className={root}>
                    <p className="bilda-export-flow__line">
                        <Text tone="success">
                            <span
                                className="bilda-export-flow__glyph"
                                aria-hidden="true"
                            >
                                {doneGlyph}
                            </span>
                            <Trans>Your package is ready.</Trans>
                        </Text>
                    </p>
                    <Text size="sm" tone="dim">
                        {state.downloadName}
                    </Text>
                    <div className="bilda-export-flow__actions">
                        {onDownload ? (
                            <Button variant="primary" onClick={onDownload}>
                                {delivery === "save" ? (
                                    <Trans>Save to disk</Trans>
                                ) : (
                                    <Trans>Download</Trans>
                                )}
                            </Button>
                        ) : null}
                        {onReset ? (
                            <Button variant="ghost" onClick={onReset}>
                                <Trans>Export again</Trans>
                            </Button>
                        ) : null}
                    </div>
                </div>
            );

        case "failed":
            return (
                <div className={root}>
                    <ErrorBanner
                        severity="danger"
                        title={state.message}
                        body={state.fix}
                        kept={t`Nothing was exported, and the material is unchanged.`}
                        action={
                            <FailedAction
                                next={state.next}
                                onRetry={onRetry}
                                onBack={onReviewFirst ?? onReset}
                            />
                        }
                        errorId={state.errorId}
                    />
                </div>
            );

        default: {
            // Exhaustiveness: a new ExportState variant is `never` here.
            const exhaustive: never = state;
            return exhaustive;
        }
    }
};

// The ONE way on after a failed export, by what has to change.
const FailedAction = ({
    next,
    onRetry,
    onBack,
}: {
    readonly next: ExportNextStep;
    readonly onRetry: (() => void) | undefined;
    readonly onBack: (() => void) | undefined;
}): ReactNode => {
    switch (next) {
        case "try-again":
            return onRetry === undefined ? null : (
                <Button variant="primary" onClick={onRetry}>
                    <Trans>Try again</Trans>
                </Button>
            );
        case "change-material":
            return onBack === undefined ? null : (
                <Button variant="primary" onClick={onBack}>
                    <Trans>Back to the material</Trans>
                </Button>
            );
        default:
            return exhaustSwitchCase(next);
    }
};
